"use client"

import { useQuery } from "@tanstack/react-query"
import { useSession } from "next-auth/react"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { BookOpen, Clock } from "lucide-react"
import Link from "next/link"
import { R2Image } from "@/components/r2-image"
import { bookPublicPath } from "@/lib/book-url"

interface RecentlyVisitedBook {
  id: string
  slug?: string | null
  title: string
  author: string
  coverUrl?: string
  genre?: string
  description?: string
  lastVisited: string
}

interface RecentlyVisitedResponse {
  books: RecentlyVisitedBook[]
}

export function RecentlyVisitedBooks() {
  const { data: session } = useSession()

  const { data, isLoading } = useQuery<RecentlyVisitedResponse>({
    queryKey: ["recently-visited"],
    queryFn: async () => {
      const response = await fetch("/api/recently-visited")
      if (!response.ok) {
        throw new Error("Failed to fetch recently visited books")
      }
      return response.json()
    },
    enabled: !!session, // Only fetch if user is logged in
  })

  // Don't render if user is not logged in
  if (!session) {
    return null
  }

  // Don't render if no books have been visited
  if (!data?.books || data.books.length === 0) {
    return null
  }

  if (isLoading) {
    return (
      <section className="py-12 bg-gradient-to-br from-slate-50 to-gray-100 dark:from-slate-900/50 dark:to-gray-900/50 rounded-2xl">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 mb-6">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-2xl font-bold">Your Recent Sips</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="animate-pulse border-0 shadow-sm">
                <CardContent className="p-3">
                  <div className="aspect-[3/4] bg-muted rounded mb-2"></div>
                  <div className="h-3 bg-muted rounded mb-1"></div>
                  <div className="h-2 bg-muted rounded w-2/3"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    )
  }

  const formatLastVisited = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 1) return "Just now"
    if (diffInHours < 24) return `${diffInHours}h ago`
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return `${diffInDays}d ago`
    return date.toLocaleDateString()
  }

  const displayedBooks = data.books.slice(0, 6)

  return (
    <section className="py-12 bg-gradient-to-br from-slate-50 to-gray-100 dark:from-slate-900/50 dark:to-gray-900/50 rounded-2xl">
      <div className="container mx-auto px-4">
        <div className="flex items-center gap-3 mb-6">
          <Clock className="h-6 w-6 text-primary" />
          <h2 className="text-2xl font-bold">Your Recent Sips</h2>
          <Badge variant="secondary" className="bg-primary/10 text-primary">
            {displayedBooks.length}
          </Badge>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {displayedBooks.map((book) => (
            <Link key={book.id} href={bookPublicPath(book)}>
              <Card className="group hover:shadow-lg transition-all duration-300 hover:scale-[1.02] cursor-pointer h-full border-0 shadow-sm bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm p-0">
                <div className="p-3 h-full flex flex-col">
                  <div className="aspect-[3/4] relative mb-3 rounded-lg overflow-hidden bg-muted flex-shrink-0 shadow-sm">
                    {book.coverUrl ? (
                      <R2Image
                        src={book.coverUrl}
                        alt={book.title}
                        fill
                        className="object-contain object-center group-hover:scale-105 transition-transform duration-300"
                        sizes="(max-width: 768px) 50vw, (max-width: 1200px) 25vw, 16vw"
                        fallbackIcon={<BookOpen className="h-8 w-8 text-muted-foreground" />}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                    <div className="absolute top-1 right-1">
                      <Badge variant="secondary" className="text-xs bg-white/90 text-black">
                        {formatLastVisited(book.lastVisited)}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="flex-1 flex flex-col justify-between min-h-[60px]">
                    <div className="space-y-1 flex-1">
                      <h3 className="font-semibold text-sm line-clamp-2 group-hover:text-primary transition-colors leading-tight">
                        {book.title}
                      </h3>
                      <p className="text-xs text-muted-foreground line-clamp-1 leading-tight">
                        by {book.author}
                      </p>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {book.genre && (
                        book.genre.split(',').map((g, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {g.trim()}
                          </Badge>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}
